// @flow

import React, { useContext } from "react";
import { useSelector } from "react-redux";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import { Link, useHistory } from "react-router-dom";
import { read, readAndReplace } from "modules/ChangeServiceForm/misc/utilities";
import { changeServiceFormInteraction } from "Commons/Utilities/GoogleTagManager";
import { scrollToPosition } from "Commons/Utilities/scrollTo";
import { ReactComponent as ArrowDown } from "Commons/Assets/icons/arrow-down-thin.svg";
import { getStepRoute } from "modules/ChangeService/Wizard/misc/Routes";
import Steps, { getSlug } from "modules/ChangeService/Wizard/misc/Steps";
import { WechselFragenBaseRoute } from "modules/ContentPages/misc/ContentPageRoutes";
import { getSingleChangeService, updateChangeService } from "modules/ChangeService/misc/Api";
import { Button, Space } from "../../../shared/CommonComponents";
import ChangeServiceFormSharedContext from "../../../../misc/context";

const Confirmation = () => {
  const {
    user,
    category: { categoryType },
    inheritedStyles,
    configurationData: {
      translations: { confirmation, glossary },
    },
  } = useContext(ChangeServiceFormSharedContext);

  const {
    formRow,
    confirmationStep,
    confirmationHeader,
    confirmationTrackingContainer,
    confirmationIconContainer,
    confirmationTrackingNoColor,
    confirmationTracking,
    confirmationTrackingIcon,
    confirmationTrackingText,
    confirmationTrackingTextDown,
    confirmationButtonContainer,
    confirmationIcon,
    formStepButton,
  } = inheritedStyles;

  const history = useHistory();
  const createdCS = useSelector(({ changeServices }) => changeServices.newlyCreatedChangeService);

  const handleYesOrNo = async (choice) => {
    const secondaryEnergyTypeExists = { secondaryEnergyTypeExists: choice === "yes" ? true : false };
    if (!isEmpty(createdCS)) {
      await updateCreatedChangeService(secondaryEnergyTypeExists);
    }
  };

  /**
  * Updates the newly created CS with the a new property (secondaryEnergyTypeExists).
    We get the change service from the back end because it must include the "version" property which is set server-side and not from the form.
  * @param {boolean} secondaryEnergyTypeExists: if the user has the other type of energy
  */
  const updateCreatedChangeService = async (secondaryEnergyTypeExists) => {
    const data = await getSingleChangeService(user.id, createdCS.id);
    const createdCSFromServer = data[createdCS.categoryId][0];
    updateChangeService(createdCSFromServer.userId, { ...createdCSFromServer, ...secondaryEnergyTypeExists });
  };

  return (
    <div className={confirmationStep}>
      <div className={formRow}>
        <div className={confirmationIcon}>
          <i className={classnames("fas fa-check-circle", "theme-color-primary")}></i>
        </div>
        <Space rem={0.5} />
        <div className={confirmationHeader}>
          {readAndReplace(confirmation.main, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </div>
      </div>
      <Space rem={1.5} />
      <div className={formRow}>
        <div className={confirmationHeader}>
          {readAndReplace(confirmation.question, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].transReverse}</span>;
              }
            },
          })}
        </div>
      </div>
      <div className={formRow}>
        <div className={confirmationIcon}>
          <i
            className={classnames(
              { "fas fa-fire-alt": categoryType.toLowerCase() === "electricity" },
              { "fas fa-fire-bolt": categoryType.toLowerCase() === "gas" },
              "theme-color-primary"
            )}
          ></i>
        </div>
      </div>
      <div className={formRow}>
        <div className={confirmationButtonContainer}>
          <Button
            primary
            onClick={async () => {
              await handleYesOrNo("yes");
              changeServiceFormInteraction("ANOTHER_PRODUCT_YES");
              history.push(
                getStepRoute(false, categoryType.toLowerCase() === "gas" ? "strom" : "gas", getSlug(Steps.CONTRACT))
              );
              scrollToPosition(0, 0);
            }}
          >
            Ja <i className="fas fa-chevron-right"></i>
          </Button>
          <Button
            basic
            style={{ color: "#7e7e7e" }}
            onClick={async () => {
              await handleYesOrNo("no");
              changeServiceFormInteraction("ANOTHER_PRODUCT_NO");
              history.push(WechselFragenBaseRoute);
              scrollToPosition(0, 0);
            }}
          >
            Nein <i className="fas fa-chevron-right"></i>
          </Button>
        </div>
      </div>
      <Space rem={2} />
      <div className={formRow}>
        <div className={confirmationHeader}>{read(confirmation.secondary)}</div>
      </div>
      <Space rem={1} />
      <div className={formRow}>
        {confirmation?.tracking.map((element, index) => (
          <div
            className={classnames(
              { ["theme-color-primary"]: element.colored, [confirmationTrackingNoColor]: !element.colored },
              confirmationTrackingContainer
            )}
            key={"csf_confirmation_tracking_" + index}
          >
            <div className={confirmationTracking}>
              <div className={confirmationIconContainer}>
                <i className={classnames(element.icon, confirmationTrackingIcon)} />
              </div>
              <span className={confirmationTrackingText}>{read(element.main)}</span>
            </div>
            {element.withArrow && (
              <div className={confirmationTracking}>
                <div className={confirmationIconContainer}>
                  <ArrowDown className={classnames(confirmationTrackingIcon)} />
                </div>
                <span className={classnames(confirmationTrackingText, confirmationTrackingTextDown)}>
                  {read(element.arrowText)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      <Space rem={1.5} />
      <div className={classnames(formRow)}>
        <Link to={WechselFragenBaseRoute} onClick={() => scrollToPosition(0, 0)}>
          <Button className={formStepButton}>
            {read(confirmation.faq)} <i className="fas fa-chevron-right"></i>
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Confirmation;
